import Fuse from 'fuse.js';
import type { FuseOptionKey } from 'fuse.js';
import { useMemo, useState } from 'react';

/**
 * The state a facet's option list runs on: what has been typed, what survives
 * it, and how much of that is held back behind a first-page slice.
 *
 * It is a hook rather than a component so the list stays open anatomy — a
 * tenant maps the surviving options into whatever parts it composes, instead of
 * handing a shared component a renderer and losing the ability to arrange them.
 * The `@vs-shop/ui` carousel is the same shape: state behind a hook, parts
 * around it.
 *
 * It decides nothing about copy or appearance. Whether the search field is
 * worth showing is answered here — it depends on how many options there are —
 * but what the field says, and whether one is composed at all, is the caller's.
 */

/**
 * Match the option's own text and nothing else. Module-level so the Fuse index
 * below is not rebuilt on every render of a caller that relies on the default.
 */
const defaultSearchKeys = [{ name: 'label', weight: 2 }];

export type UseFilterRailOptionsParameters<T> = {
  readonly items: readonly T[];
  /**
   * Which fields a query is matched against. Defaults to the option's `label`;
   * a facet whose options carry a second searchable field — a brand's code, a
   * value behind a display name — names it here so typing that finds them.
   */
  readonly searchKeys?: FuseOptionKey<T>[] | undefined;
  /** Fuse fuzziness, lower being stricter. */
  readonly searchThreshold?: number | undefined;
  /**
   * How many options render before "show all". Omit to render every surviving
   * option with no such affordance.
   */
  readonly initialVisibleCount?: number | undefined;
  /** How many options a facet needs before a search field earns its space. */
  readonly minItemsForSearch?: number | undefined;
};

export type UseFilterRailOptionsReturn<T> = {
  readonly query: string;
  readonly setQuery: (query: string) => void;
  /** The options to render: matched by the query, then cut to the slice. */
  readonly visibleItems: readonly T[];
  /** Whether this facet has enough options for a search field to be worth showing. */
  readonly isSearchable: boolean;
  /** Whether the slice is currently holding anything back. */
  readonly hasHiddenItems: boolean;
  readonly showAll: () => void;
};

export const useFilterRailOptions = <T extends { label: string }>({
  items,
  searchKeys = defaultSearchKeys,
  searchThreshold = 0.4,
  initialVisibleCount,
  minItemsForSearch = 10,
}: UseFilterRailOptionsParameters<T>): UseFilterRailOptionsReturn<T> => {
  const [query, setQuery] = useState('');
  const [isShowingAll, setIsShowingAll] = useState(false);

  const fuse = useMemo(
    () =>
      new Fuse(items, {
        keys: searchKeys,
        threshold: searchThreshold,
        minMatchCharLength: 1,
        ignoreLocation: true,
      }),
    [items, searchKeys, searchThreshold]
  );

  const matchedItems = useMemo(() => {
    if (!query.trim()) {
      return items;
    }

    return fuse.search(query).map((result) => result.item);
  }, [fuse, items, query]);

  const visibleItems =
    initialVisibleCount !== undefined && !isShowingAll
      ? matchedItems.slice(0, initialVisibleCount)
      : matchedItems;

  return {
    query,
    setQuery,
    visibleItems,
    isSearchable: items.length >= minItemsForSearch,
    hasHiddenItems: visibleItems.length !== matchedItems.length,
    showAll: () => {
      setIsShowingAll(true);
    },
  };
};
